import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiLock, FiShield, FiMail, FiUser, FiAtSign, FiCheck, FiX, FiUsers } from 'react-icons/fi';
import { LuShieldCheck, LuImage, LuKeyRound, LuHardDrive } from 'react-icons/lu';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, reason: '' });
  const [isLoading, setIsLoading] = useState(false);
  const submittingRef = useRef(false);
  const [signupError, setSignupError] = useState('');
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [createWorkspace, setCreateWorkspace] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [inviteToken, setInviteToken] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoInfo, setPromoInfo] = useState(null);
  const [webAuthnStatus, setWebAuthnStatus] = useState({ available: false, hasApi: false, isSecureContext: false });
  const { login, loginWithPasskey, signup, forceChangePassword, error, auth, checkAuth, loadingMessage, isAuthenticating } = useAuth();
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const navigate = useNavigate();
  function continueAfterLogin() {
    const claimReturn = sessionStorage.getItem('photosClaimReturn');
    sessionStorage.removeItem('photosClaimReturn');
    navigate(/^\/claim\/[A-Za-z0-9_-]+$/.test(claimReturn || '') ? claimReturn : '/');
  }
  const location = useLocation();
  useEffect(() => {
    if (auth?.getWebAuthnSupport) {
      setWebAuthnStatus(auth.getWebAuthnSupport());
    } else {
      const available = auth.isWebAuthnSupported();
      setWebAuthnStatus({ available, hasApi: available, isSecureContext: window?.isSecureContext ?? false });
    }
  }, [auth]);

  useEffect(() => {
    if (!isLogin && createWorkspace && !workspaceName && name) {
      const baseName = name.split(' ')[0] || name;
      setWorkspaceName(`${baseName} Studio`);
    }
  }, [name, isLogin, createWorkspace, workspaceName]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const mode = params.get('mode');
    if (mode === 'signup') setIsLogin(false);
    const planParam = params.get('plan');
    if (planParam && ['free', 'pro', 'studio', 'business'].includes(planParam)) {
      setSelectedPlan(planParam);
      setIsLogin(false);
    }
    const promoParam = params.get('promo');
    const storedPromo = (() => { try { return localStorage.getItem('promoCode'); } catch { return null; } })();
    if (promoParam || storedPromo) {
      const code = (promoParam || storedPromo).trim().toUpperCase();
      setPromoCode(code);
      try { localStorage.setItem('promoCode', code); } catch {}
      if (promoParam) setIsLogin(false);
    }
    const token = params.get('token') || (auth?.getPendingInviteToken ? auth.getPendingInviteToken() : null);
    if (token) {
      if (auth?.setPendingInviteToken) {
        auth.setPendingInviteToken(token);
      }
      setInviteToken(token);
      setCreateWorkspace(false);
    }
  }, [location.search, auth]);

  // Look up the promo's discount so the plan prices shown reflect it.
  useEffect(() => {
    if (!promoCode) { setPromoInfo(null); return; }
    let cancelled = false;
    fetch(`/api/promo/stripe/${encodeURIComponent(promoCode)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setPromoInfo(d && d.valid ? d : null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [promoCode]);


  // Check username availability with debounce (public check during signup)
  const checkUsername = useCallback(async (usernameToCheck) => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setUsernameStatus({ checking: false, available: null, reason: '' });
      return;
    }

    // Validate format first
    const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    if (!usernameRegex.test(usernameToCheck)) {
      setUsernameStatus({
        checking: false,
        available: false,
        reason: 'Must be 3-20 characters, letters, numbers, _ or - only'
      });
      return;
    }

    setUsernameStatus({ checking: true, available: null, reason: '' });

    try {
      // Use public endpoint for checking during signup
      const res = await fetch(`/api/auth/username/available?username=${encodeURIComponent(usernameToCheck)}`);
      const data = await res.json();
      setUsernameStatus({
        checking: false,
        available: data.available,
        reason: data.reason || (data.available ? 'Username is available!' : '')
      });
    } catch (err) {
      setUsernameStatus({ checking: false, available: null, reason: 'Failed to check' });
    }
  }, []);

  // Debounce username check
  useEffect(() => {
    if (isLogin || !username) return;

    const timer = setTimeout(() => {
      checkUsername(username);
    }, 400);

    return () => clearTimeout(timer);
  }, [username, isLogin, checkUsername]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSignupError('');
    setIsLoading(true);
    try {

      let success = false;
      if (isLogin) {
        const result = await login(email, password);

        // Check if passkey login is required (user has passkeys registered)
        if (result && typeof result === 'object' && result.passkeyRequired) {
          // Auto-trigger passkey login
          setIsLoading(false);
          setIsPasskeyLoading(true);
          const passkeySuccess = await loginWithPasskey(email);
          if (passkeySuccess) {
            continueAfterLogin();
          }
          setIsPasskeyLoading(false);
          return;
        }

        if (result && typeof result === 'object' && result.mustChangePassword) {
          setMustChangePassword(true);
          setIsLoading(false);
          return;
        }
        success = !!result;
      } else {
        // Validate username is available before signup
        if (username && usernameStatus.available !== true) {
          setIsLoading(false);
          return;
        }

        success = await signup({
          email,
          password,
          name,
          plan: selectedPlan,
          workspaceName,
          createWorkspace
        });

        // If signup succeeded and we have a username, set it
        if (success && username) {
          try {
            const usernameResponse = await fetch('/api/auth/username', {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ username })
            });
            if (!usernameResponse.ok) {
              const body = await usernameResponse.json().catch(() => ({}));
              throw new Error(body.message || body.error || 'Please choose another username or retry.');
            }
          } catch (err) {
            setSignupError(`Your account was created, but your username was not saved. ${err.message}`);
            return;
          }
        }
      }
      if (success) {
        if (!isLogin && selectedPlan) {
          try {
            const checkoutRes = await fetch('/api/subscription/checkout', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ tier: selectedPlan, source: 'signup', promoCode: promoCode || undefined })
            });
            const checkoutData = await checkoutRes.json();
            if (checkoutData.checkoutUrl && checkoutData.checkoutUrl !== '#') {
              try { localStorage.removeItem('promoCode'); } catch {}
              window.location.href = checkoutData.checkoutUrl;
              return;
            }
          } catch (err) {
            console.error('Failed to create checkout session:', err);
          }
        }
        continueAfterLogin();
      }

    } catch (err) {
      setSignupError(err.message || 'Could not complete sign in. Please retry.');
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError('Password must include an uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setPasswordError('Password must include a lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must include a number');
      return;
    }
    if (!/[@$!%*?&]/.test(newPassword)) {
      setPasswordError('Password must include a special character (@$!%*?&)');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    const success = await forceChangePassword(newPassword);
    if (success) {
      continueAfterLogin();
    } else {
      setPasswordError(error || 'Failed to update password');
    }
    setIsLoading(false);
  }

  async function handlePasskeyLogin() {
    if (!email) return;

    setIsPasskeyLoading(true);
    const success = await loginWithPasskey(email);
    if (success) {
      continueAfterLogin();
    }
    setIsPasskeyLoading(false);
  }

  const plans = [
    { id: 'free', name: 'Personal', price: '$7/mo', amount: 7, storage: '100 GB' },
    { id: 'pro', name: 'Pro', price: '$39/mo', amount: 39, storage: '500 GB' },
    { id: 'studio', name: 'Studio', price: '$99/mo', amount: 99, storage: '2 TB' },
    { id: 'business', name: 'Business', price: '$179/mo', amount: 179, storage: '5 TB' },
  ];

  const promoPrice = (p) => (p.amount * (1 - (promoInfo?.percentOff || 0) / 100)).toFixed(2).replace(/\.00$/, '');

  // Compact dropdown label (native <select> options can't wrap/truncate gracefully).
  const planLabel = (p) => {
    if (promoInfo?.valid && promoInfo.percentOff) {
      return `${p.name}, $${promoPrice(p)}/mo (${p.storage})`;
    }
    return `${p.name}, ${p.price} (${p.storage})`;
  };

  return (
    <div className="login-page">
      <div className="login-layout">
        <div className="login-stack">
          <div className="login-card login-card--primary">
          {/* Logo */}
          <div className="login-logo-wrap">
            <img src="/brand/logo-primary-darkteal.png" alt="Photo Harbor" className="login-logo-img" />
          </div>

          {/* Title */}
          <div className="login-header">
            <span className="login-kicker">Photo Harbor</span>
            <h1 className="login-title">
              {mustChangePassword ? 'Set your password' : isLogin ? 'Sign in' : 'Create account'}
            </h1>
            {mustChangePassword && (
              <p style={{ color: '#5f7270', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Please choose a new password for your account.
              </p>
            )}
          </div>

          {mustChangePassword ? (
          <form onSubmit={handlePasswordChange} className="login-form">
            <div className="login-field">
              <FiLock className="login-field-icon" />
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                required
                autoFocus
                className="login-input"
              />
            </div>
            <div className="login-field">
              <FiLock className="login-field-icon" />
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                required
                className="login-input"
              />
            </div>
            <div style={{ fontSize: '0.75rem', color: '#7d8f8d', marginBottom: '0.5rem' }}>
              8+ characters with uppercase, lowercase, number, and special character (@$!%*?&)
            </div>
            {(passwordError || error) && (
              <div className="login-error">{passwordError || error}</div>
            )}
            <button
              type="submit"
              className="login-btn login-btn-primary"
              disabled={isLoading || isAuthenticating}
            >
              {isLoading && <span className="login-spinner" aria-hidden />}
              {isLoading ? 'Updating password...' : 'Set Password & Continue'}
            </button>
          </form>
          ) : (
          <form onSubmit={handleSubmit} className="login-form">
            {inviteToken && (
              <div className="login-invite-banner">
                <FiUsers size={18} />
                <div>
                  <strong>Team invite detected.</strong> Sign in or create an account to join this workspace.
                </div>
              </div>
            )}

            {!isLogin && (
              <div className="login-field">
                <FiUser className="login-field-icon" />
                <input
                  type="text"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isLoading}
                  className="login-input"
                />
              </div>
            )}

            {!isLogin && (
              <div className="login-field login-field-username">
                <FiAtSign className="login-field-icon" />
                <input
                  type="text"
                  placeholder="Choose a username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  required
                  disabled={isLoading}
                  maxLength={20}
                  className="login-input"
                />
                {usernameStatus.checking && (
                  <span className="login-field-status checking">checking...</span>
                )}
                {!usernameStatus.checking && usernameStatus.available === true && (
                  <FiCheck className="login-field-status available" />
                )}
                {!usernameStatus.checking && usernameStatus.available === false && (
                  <FiX className="login-field-status unavailable" />
                )}
              </div>
            )}

            {!isLogin && usernameStatus.reason && (
              <p className={`login-username-hint ${usernameStatus.available ? 'available' : 'unavailable'}`}>
                {usernameStatus.reason}
              </p>
            )}

            <div className="login-field">
              <FiMail className="login-field-icon" />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="login-input"
              />
            </div>

            <div className="login-field">
              <FiLock className="login-field-icon" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="login-input"
              />
            </div>

            {isLogin && <Link to="/forgot-password" className="login-forgot-password" style={{ display: 'inline-block', padding: '0.75rem 0', textDecoration: 'underline' }}>Forgot password?</Link>}

            {!isLogin && (
              <div className="login-field-select">
                <label className="login-label">Choose a plan</label>
                {promoInfo?.valid && promoInfo.percentOff && (
                  <div className="login-promo-banner">
                    🎉 Code <strong>{promoCode}</strong> applied, {promoInfo.percentOff}% off
                    {promoInfo.durationInMonths ? ` for your first ${promoInfo.durationInMonths} months` : ''}.
                  </div>
                )}
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  disabled={isLoading}
                  required
                  className="login-select"
                >
                  <option value="" disabled>Select a plan</option>
                  {plans.map(p => (
                    <option key={p.id} value={p.id}>
                      {planLabel(p)}
                    </option>
                  ))}
                </select>
                {promoInfo?.valid && promoInfo.percentOff && selectedPlan && (() => {
                  const p = plans.find(x => x.id === selectedPlan);
                  if (!p) return null;
                  const months = promoInfo.durationInMonths || 12;
                  return (
                    <p className="login-promo-note">
                      <strong>${promoPrice(p)}/mo</strong> for your first {months} months, then ${p.amount}/mo. Cancel anytime.
                    </p>
                  );
                })()}
              </div>
            )}

            {!isLogin && !inviteToken && (
              <div className="login-field-toggle">
                <label className="login-toggle-field">
                  <input
                    type="checkbox"
                    checked={createWorkspace}
                    onChange={(e) => setCreateWorkspace(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span>Create a team workspace</span>
                </label>
              </div>
            )}

            {!isLogin && createWorkspace && !inviteToken && (
              <div className="login-field">
                <FiUsers className="login-field-icon" />
                <input
                  type="text"
                  placeholder="Workspace name"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  required
                  disabled={isLoading}
                  className="login-input"
                />
              </div>
            )}

            {(signupError || error) && (
              <div className="login-error">
                {signupError || error}
              </div>
            )}

            {error && auth.isAuthenticated() && (
              <button type="button" disabled={isAuthenticating} onClick={async () => {
                if (await checkAuth()) continueAfterLogin();
              }}>Retry connection</button>
            )}

            <button
              type="submit"
              className="login-btn login-btn-primary"
              disabled={isLoading || isPasskeyLoading || !email || !password || (!isLogin && (!name || !username || !selectedPlan || usernameStatus.available !== true || (createWorkspace && !workspaceName)))}
            >
              {isLoading && <span className="login-spinner" aria-hidden />}
              {isLoading
                ? (loadingMessage || (isLogin ? 'Signing in...' : 'Creating account...'))
                : (isLogin ? 'Sign in' : (promoInfo?.valid ? 'Continue to payment' : 'Start 14-day free trial'))}
            </button>
          </form>
          )}

          {/* Passkey login - hidden during forced password change */}
          {isLogin && !mustChangePassword && webAuthnStatus.available && (
            <div className="login-divider">
              <span>or</span>
            </div>
          )}

          {isLogin && !mustChangePassword && webAuthnStatus.available && (
            <button
              type="button"
              onClick={handlePasskeyLogin}
              disabled={isLoading || isPasskeyLoading || !email}
              className="login-btn login-btn-passkey"
            >
              {isPasskeyLoading ? <span className="login-spinner" aria-hidden /> : <FiShield size={18} />}
              {isPasskeyLoading ? (loadingMessage || 'Authenticating...') : 'Sign in with Passkey'}
            </button>
          )}

          {isLogin && !mustChangePassword && !webAuthnStatus.available && webAuthnStatus.hasApi && !webAuthnStatus.isSecureContext && (
            <p className="login-passkey-note">
              Passkeys require HTTPS or localhost
            </p>
          )}
          </div>


        </div>

        <div className="login-side">
          <div className="login-side-panel">
            <div className="login-side-hero">
              <h2>Your photos deserve better than Big Tech.</h2>
              <p>Professional storage built on privacy, not advertising.</p>
            </div>
            <div className="login-side-features">
              <div className="login-side-feature">
                <div className="login-side-feature-icon"><LuShieldCheck size={18} /></div>
                <div>
                  <strong>No AI training</strong>
                  <span>Your photos are never used to train models</span>
                </div>
              </div>
              <div className="login-side-feature">
                <div className="login-side-feature-icon"><LuImage size={18} /></div>
                <div>
                  <strong>Original quality</strong>
                  <span>RAW, HEIC, JPEG stored without compression</span>
                </div>
              </div>
              <div className="login-side-feature">
                <div className="login-side-feature-icon"><LuKeyRound size={18} /></div>
                <div>
                  <strong>You own your data</strong>
                  <span>Export everything anytime. No lock-in.</span>
                </div>
              </div>
              <div className="login-side-feature">
                <div className="login-side-feature-icon"><LuHardDrive size={18} /></div>
                <div>
                  <strong>14-day free trial</strong>
                  <span>All plans. Cancel anytime.</span>
                </div>
              </div>
            </div>
            <div className="login-side-footer">
              <span className="login-badge">Photo Harbor</span>
              <span className="login-side-powered">Powered by Dailey OS</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
